import type { Tenant } from '../../../../configuration/types';
import type { Driver, DriverToCreate } from '../../../../types';
import { CreateDriverDialog } from '../components/CreateDriverDialog';
import {
    driverCreationFinished,
    driverCreationStarted,
    driverSelected,
    getDriverCreationInProgressRtk,
    getDriversRtk,
    getShowDriverCreationDialogRtk,
    toggleDriverCreationDialog,
} from '../../appSlice';
import { getTenant, getUserAccount } from '../../../../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { useAddIdentificationMutation, useLazyGetDriverQuery, useSaveDriverMutation } from '../../services/driversApi';
import { setSelectedDriver } from '../../drivers/sidebar/sidebarSlice';
import { showErrorNotification, showSuccessNotification } from '../../notifications/notifications';
import { ErrorOrigin } from '../../services/apiUtils';
import { getApiErrorCode, isApiErrorWithCode } from '../../services/apiErrors';

export interface CreateDriverDialogPropsFromState {
    existingDrivers: Driver[];
    accountId: string | undefined;
    tenant: Tenant | null;
    showDialog: boolean;
    driverCreationInProgress: boolean;
}

export interface CreateDriverDialogPropsFromDispatch {
    createAndSelectDriver: (driver: DriverToCreate) => void;
    toggleDriverCreationDialog: (show: boolean) => void;
}

const mapToDriver = (driverToCreate: DriverToCreate): Driver => ({
    driverId: driverToCreate.driverId,
    accountId: driverToCreate.accountId,
    firstName: driverToCreate.firstName ?? '',
    lastName: driverToCreate.lastName,
    email: driverToCreate.email,
    phoneNumber: driverToCreate.phoneNumber,
    status: driverToCreate.status,
    activeCardNumber: driverToCreate.identification.value,
    otherCardNumbers: [],
    identifications: [driverToCreate.identification],
    tags: [],
});

export const CreateDriverDialogContainer = () => {
    const dispatch = useAppDispatch();

    const existingDrivers = useAppSelector(getDriversRtk);
    const accountId = useAppSelector(getUserAccount);
    const tenant = useAppSelector(getTenant);
    const showDialog = useAppSelector(getShowDriverCreationDialogRtk);
    const driverCreationInProgress = useAppSelector(getDriverCreationInProgressRtk);

    const [saveDriverMutation] = useSaveDriverMutation();
    const [addIdentificationMutation] = useAddIdentificationMutation();
    const [loadDriver] = useLazyGetDriverQuery();

    const createAndSelectDriver = async (driver: DriverToCreate) => {
        dispatch(driverCreationStarted());
        let hasCreatedDriver = false;

        try {
            await saveDriverMutation(mapToDriver(driver)).unwrap();
            hasCreatedDriver = true;

            try {
                await addIdentificationMutation({
                    driverId: driver.driverId,
                    identification: driver.identification,
                }).unwrap();
                showSuccessNotification('intl-msg:save.success');
            } catch (error) {
                if (isApiErrorWithCode(error)) {
                    showErrorNotification(getApiErrorCode(error, ErrorOrigin.DRIVER_ADMINISTRATION));
                }
            }
        } catch (error) {
            if (isApiErrorWithCode(error)) {
                showErrorNotification(getApiErrorCode(error, ErrorOrigin.DRIVER_ADMINISTRATION));
            }
        } finally {
            if (hasCreatedDriver) {
                const createdDriver = await loadDriver(driver.driverId, true).unwrap();
                dispatch(driverSelected(driver.driverId));
                dispatch(setSelectedDriver(createdDriver));
            }
            dispatch(driverCreationFinished());
        }
    };

    return (
        <CreateDriverDialog
            existingDrivers={existingDrivers}
            accountId={accountId}
            tenant={tenant}
            showDialog={showDialog}
            driverCreationInProgress={driverCreationInProgress}
            createAndSelectDriver={createAndSelectDriver}
            toggleDriverCreationDialog={show => {
                dispatch(toggleDriverCreationDialog(show));
            }}
        />
    );
};
